import {
  Box,
  chakra,
  Button as ChakraButton,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Switch,
  Text,
} from '@chakra-ui/react';
import {
  ColumnFilter,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AccessLinkTypes, AdminMeetingListViewQuery } from '@/@generated/codegen/API';
import { AccessLinkPanelClient } from '@/components/AccessLinkPanel/AccessLinkPanelClient';
import {
  getMeetingStatus,
  getMeetingStatusText,
} from '@/features/meeting/helpers/getMeetingStatus';
import getNonDeletableMeetingIds from '@/features/meeting/helpers/getNonDeletableMeetings';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { calculateMeetingDuration } from '@/utils/calculateMeetingDuration';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminMeetingTableStore from './AdminMeetingTableStore';
import getColumns, { AdminMeetingListItemType } from './columns';
import { CreateMeetingForm } from '../CreateMeetingForm/CreateMeetingForm';
import MeetingUserAccessList from '../MeetingUserAccessList/MeetingUserAccessList';

type Props = {
  data: AdminMeetingListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  tablePrefix: string;
};

const filterExtraInfos: FilterExtraInfo<AdminMeetingListItemType> = [
  {
    filterId: 'eventDate',
    filterType: FilterType.DATE,
  },
  {
    filterId: 'isInTesting',
    filterType: FilterType.BOOLEAN,
  },
  {
    filterId: 'isArchived',
    filterType: FilterType.BOOLEAN,
  },
  {
    filterId: 'isDemo',
    filterType: FilterType.BOOLEAN,
  },
  {
    filterId: 'caseCluster',
    filterType: FilterType.STRING,
    queryParamKey: 'caseClusterName',
  },
  {
    filterId: 'clients',
    filterType: FilterType.STRING,
    queryParamKey: 'clientName',
  },
  {
    filterId: 'teams',
    filterType: FilterType.STRING,
    queryParamKey: 'teamName',
  },
  {
    filterId: 'host',
    filterType: FilterType.STRING,
    queryParamKey: 'hostName',
  },
];

const AdminMeetingListView = ({ data, pagination, tablePrefix }: Props) => {
  const { t } = useTranslation();
  const totalCount = data.listMeetings.totalCount;
  const tableData: AdminMeetingListItemType[] = useMemo(() => {
    const meetingList = data.listMeetings.edges.map((edge) => edge.node);
    return meetingList.map((meeting) => {
      const meetingStatus = getMeetingStatus(meeting);
      return {
        ...meeting,
        meetingStatus: meetingStatus,
        meetingStatusText: getMeetingStatusText(meetingStatus),
        meetingDurationMinutes: calculateMeetingDuration(meeting),
      };
    });
  }, [data.listMeetings]);
  const nonDeletableMeetings = getNonDeletableMeetingIds(tableData);
  const [showMeetingForm, setShowMeetingForm] = useState<boolean>(false);
  const [isDupingMeeting, setIsDupingMeeting] = useState<boolean>(false);
  const [meetingData, setMeetingData] = useState<AdminMeetingListItemType | undefined>(undefined);
  const [managedMeetingID, setManagedMeetingID] = useState<string | undefined>(undefined);
  const managedMeeting = tableData.find((m) => m.id === managedMeetingID)!;
  const [showAccessFormMeeting, setShowAccessFormMeeting] = useState<boolean>(false);
  const [showAccessLinkPanel, setShowAccessLinkPanel] = useState<boolean>(false);
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfos, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminMeetingTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);
  const handleDupeMeeting = (row: AdminMeetingListItemType) => {
    setShowMeetingForm(true);
    setMeetingData(row);
    setIsDupingMeeting(true);
  };

  const handleEditMeeting = (row: AdminMeetingListItemType) => {
    setShowMeetingForm(true);
    setMeetingData(row);
  };
  const columns = getColumns({
    nonDeletableMeetings,
    onDupeClick: handleDupeMeeting,
    onEditClick: handleEditMeeting,
    onUserAccess: (meeting: AdminMeetingListItemType) => {
      setManagedMeetingID(meeting.id);
      setShowAccessFormMeeting(true);
    },
    onAccessLinkClick: (meeting: AdminMeetingListItemType) => {
      setManagedMeetingID(meeting.id);
      setShowAccessLinkPanel(true);
    },
  });

  const table = useReactTable({
    data: tableData,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listMeetings.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  // --------------------- handlers ------------------------

  const handleCloseForm = useCallback(() => {
    setMeetingData(undefined);
    setIsDupingMeeting(false);
    setShowMeetingForm(false);
  }, []);

  const handleOnShowArchivedChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.currentTarget.checked;
      const newFilters = columnFilters.filter((el) => el.id !== 'isArchived');

      newFilters.push({
        id: 'isArchived',
        value: newValue,
      });
      onColumnFiltersChange(newFilters);
    },
    [columnFilters]
  );

  const titleLabel = useMemo(() => {
    if (meetingData?.name) {
      if (isDupingMeeting) {
        return t('admin.meeting.button.duplicateMeeting', { meetingName: meetingData.name });
      }
      return t('admin.meeting.button.updateMeeting', { meetingName: meetingData.name });
    }
    return t('admin.meeting.button.createnewMeeting');
  }, [meetingData, isDupingMeeting]);

  // --------------------- render ------------------------

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      {managedMeeting && showAccessFormMeeting && (
        <MeetingUserAccessList
          meeting={managedMeeting}
          onClose={() => {
            setShowAccessFormMeeting(false);
          }}
        />
      )}
      {showAccessLinkPanel && managedMeetingID && (
        <AccessLinkPanelClient
          type={AccessLinkTypes.MeetingReadAccess}
          objId={managedMeetingID}
          onClose={() => {
            setManagedMeetingID(undefined);
            setShowAccessLinkPanel(false);
          }}
        />
      )}
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          Meetings
        </chakra.h3>

        <ChakraButton
          background="blue.400"
          color="white"
          size={{ base: 'sm', md: 'md' }}
          data-test="create-meeting-button-popUp"
          onClick={() => setShowMeetingForm(true)}
        >
          {t('admin.meeting.button.createnewMeeting')}
        </ChakraButton>

        <Modal isOpen={showMeetingForm} onClose={() => handleCloseForm()}>
          <ModalOverlay />
          <ModalContent w="full" maxW={{ base: '95vw', md: '800px' }}>
            <ModalCloseButton />
            <ModalBody w="full">
              <Flex flexDir="column" w="full" p="20px" gap="10px">
                <Heading size="sm" pb="5">
                  {titleLabel}
                </Heading>

                <CreateMeetingForm
                  closeMeetingForm={handleCloseForm}
                  meeting={meetingData}
                  isDuplicateMeeting={isDupingMeeting}
                />
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>

      {data && (
        <>
          <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
            <ColumnVisibility table={table} />
            <Filters
              columnFilters={columnFilters}
              hiddenFilters={['isArchived']}
              setColumnFilters={onColumnFiltersChange}
              table={table}
              filterExtraInfo={filterExtraInfos}
            />
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={onGlobalFilterChange}
              maxWidth={{ base: 'none', md: '250px' }}
              flex={{ base: 1, md: 'initial' }}
              minW="150px"
            />
            <ActiveFilters
              columnFilters={columnFilters}
              hiddenFilters={['isArchived']}
              removeItemFunc={(elem) => {
                onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
              }}
              filterExtraInfo={filterExtraInfos}
            />
            <Flex
              direction="row"
              userSelect="none"
              bgColor="gray.200"
              p={{ base: '6px', md: '10px' }}
              borderRadius="20px"
              align="center"
              gap="5px"
              h="fit-content"
              whiteSpace="nowrap"
              ml="auto"
            >
              <Switch
                size={{ base: 'md', md: 'lg' }}
                isChecked={
                  columnFilters.find((el: ColumnFilter) => el.id === 'isArchived')?.value === true
                }
                onChange={handleOnShowArchivedChange}
              />
              <Text display="inline-block" fontSize={{ base: 'xs', md: 'sm' }}>
                Show Only Archived meetings
              </Text>
            </Flex>
            <Pagination
              table={table}
              pageInfo={data.listMeetings.pageInfo}
              totalRows={totalCount}
              tablePrefix={tablePrefix}
            />
          </Flex>

          <TanstackTableView table={table} />
        </>
      )}
    </Box>
  );
};

export default AdminMeetingListView;
